<template>
  <Wrapper :join="join">
    <div class="row">
      <div class="d-flex col-12">
        <SegmentControl
          v-model="accountGender"
          v-bind="{
            id: 'genderPicker',
            options: genderItems,
            label: $$t('Gender', 'label-gender'),
          }"
          class="mb-3 pb-2"
        />
      </div>
      <div class="col-12 col-lg-6 mb-4">
        <TextInput
          v-model="accountDisplayName"
          :label="$$t('Display Name', 'label-display-name')"
          :placeholder="$$t('Display Username', 'placeholder-display-name')"
          @blur="checkDisplayName"
        />
      </div>
      <div class="col-12 col-lg-6 mb-4">
        <TextInput
          v-model="accountVanityId"
          v-validate="{ regex: /^[\w-_]+$/ }"
          :label="$t('_web_entity-edit-vanity-id-label', 'Vanity url')"
          :placeholder="
            $t('_web_entity-edit-vanity-id-placeholder', 'Vanity url')
          "
          name="vanity_id"
          :error="errors.first('vanity_id')"
          @blur="checkVanityId"
        />
      </div>
      <div class="user-identity-wrapper">
        <div class="">
          <span>E-mail</span>
          <div
            v-if="identity.email"
            class="user-identity-email"
            @click="updateEmail"
          >
            {{ identity.email }}
          </div>
        </div>
        <div class="">
          <span>Phone number</span>
          <div
            v-if="identity.phone"
            class="user-identity-phone"
            @click="updatePhone"
          >
            +{{ identity.phone.country_code }}
            {{ identity.phone.national_number }}
          </div>
          <div
            v-else
            class="user-identity-phone add-new-phone"
            @click="updatePhone"
          >
            Add new phone
          </div>
        </div>
      </div>
      <div class="col-6 col-lg-auto mb-4">
        <DatePicker
          v-model="accountBirthDate"
          name="userBirthDate"
          :label="$$t('Date of Birth', 'label-birth')"
          :placeholder="$$t('Select Date', 'placeholder-birth')"
          :max-date="getNowSeconds()"
        />
      </div>
      <AccountEditCountry class="col-6 col-lg-6" />
      <AccountEditCity class="col-12 col-lg-6" />
      <Description class="col-12" />
    </div>
  </Wrapper>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';

import { translate } from '@/mixins/translate';
import { vanityUrlEdit } from '@/views/account/content/edit/panels/mixins/vanityUrlEdit';
import Wrapper from '../../PanelWrapper';
import dateMethods from '@/mixins/dateMethods';
import { prop } from '@/utils/factories';
import TextInput from '@/components/atoms/form/HeraFormTextInput';
import AccountEditCountry from './elements/Country';
import AccountEditCity from './elements/City';
import DatePicker from '@/components/form/DatePicker';
import Description from './Description';
import SegmentControl from '@/components/atoms/form/HeraFormSegmentControl';

export default {
  name: 'AccountEditInformationProfile',
  components: {
    TextInput,
    Wrapper,
    AccountEditCountry,
    AccountEditCity,
    DatePicker,
    Description,
    SegmentControl,
  },
  inject: ['$validator'],
  mixins: [...dateMethods, translate('_web_profile-edit_info'), vanityUrlEdit],
  props: {
    join: prop(String),
  },
  data() {
    return {
      identity: null,
    };
  },
  computed: {
    ...mapGetters(['accountEditData']),
    ...mapState('my', ['accountId']),
    genderItems() {
      return [
        { label: this.$$t('Male', 'gender-male'), value: 'Male', id: 'Male' },
        {
          label: this.$$t('Female', 'gender-female'),
          value: 'Female',
          id: 'Female',
        },
      ];
    },
    accountGender: {
      get() {
        return this.accountEditData.gender || '';
      },
      set(gender) {
        this.updateEditData({ gender });
      },
    },
    accountBirthDate: {
      get() {
        return this.accountEditData.birth_date;
      },
      set(date) {
        this.updateEditData({
          birth_date: date,
        });
      },
    },
    accountDisplayName: {
      get() {
        if (typeof this.accountEditData.display_name !== 'string') {
          return this.accountId;
        }

        return this.accountEditData.display_name;
      },
      set(displayName) {
        displayName = displayName.replace(/[<>="]/g, '');
        this.updateEditData({
          display_name: displayName,
        });
      },
    },
    currentCountryData() {
      const { country, country_id } = this.accountEditData;
      return country
        ? {
            id: country_id,
            name: country,
          }
        : null;
    },
    accountCity: {
      get() {
        return this.accountEditData.city;
      },
      set(city) {
        this.updateEditData({
          city: city ? city.value : '',
          city_id: city ? city.id : '',
        });
      },
    },
  },
  mounted() {
    this.getIdentity().then(res => (this.identity = res));
  },
  methods: {
    ...mapActions(['getIdentity', 'openModal']),
    ...mapMutations(['updateEditData']),
    handleDateChange(date) {
      let result = 0;
      if (date) {
        let dateArr = date
          .replace(/^([0-9]+-[0-9]+-[0-9]+).+$/, '$1')
          .split('-');
        result = Date.UTC(dateArr[0], +dateArr[1] - 1, +dateArr[2]) / 1000;
      }

      this.updateEditData({ birth_date: result });
    },
    checkDisplayName() {
      if (!(this.accountDisplayName || '').trim()) {
        this.accountDisplayName = this.accountId;
      }
    },
    updatePhone() {
      this.openModal({
        name: 'MobileProvide',
        settings: {
          variant: 'fit-content',
        },
        options: {
          clickOnBackdrop: false,
          escKey: false,
        },
        props: {
          isEditing: true,
          isSso: this.identity.sns_signin_only,
        },
        events: {
          success: () => {
            this.getIdentity().then(res => (this.identity = res));
          },
        },
      });
    },
    updateEmail() {
      this.openModal({
        name: 'EmailProvide',
        settings: {
          variant: 'fit-content',
        },
        options: {
          clickOnBackdrop: false,
          escKey: false,
        },
        props: {
          isSso: this.identity.sns_signin_only,
        },
        events: {
          success: () => {
            this.getIdentity().then(res => (this.identity = res));
          },
        },
      });
    },
  },
};
</script>
<style lang="scss">
.date-picker {
  width: 100%;
  .mx-input-wrapper {
    .mx-input {
      max-width: none !important;
    }
  }
}
@media (max-width: 767px) {
  .date-picker {
    width: 100%;
    .mx-input-wrapper {
      .mx-icon-calendar {
        right: 5px !important;
        .app-rtl & {
          right: unset !important;
        }
      }
    }
  }
}

.user-identity-wrapper {
  display: flex;
  width: 100%;
  padding-left: 15px;
  padding-right: 15px;
  gap: 30px;
  margin-bottom: 1rem;
  font-size: 14px;
  @media (max-width: 767px) {
    flex-direction: column;
  }

  .user-identity-email,
  .user-identity-phone {
    padding: 10px;
    background-color: #1c212b;
    border-radius: 5px;
    margin-top: 8px;

    &:hover {
      cursor: pointer;
      background-color: rgb(37, 42, 53);
    }
  }

  .user-identity-email {
    min-width: 215px;
  }
}

.add-new-phone {
  color: gray;
}
</style>
